import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { validateContactForm } from '../../../utils/formValidation';
import './Contact.css';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from 'emailjs-com';

const Contact = ({ isVisible, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [focusedField, setFocusedField] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [animatedItems, setAnimatedItems] = useState([]);
  
  const containerRef = useRef(null);
  const particlesRef = useRef(null);
  const formRef = useRef(null);
  
  // Variables pour les particules
  const numParticles = 60;
  const particles = useRef([]);
  
  // Initialisation des particules
  useEffect(() => {
    if (isVisible && particlesRef.current) {
      for (let i = 0; i < numParticles; i++) {
        particles.current.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
      
      const canvas = particlesRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      animateParticles();
    }
    
    // Nettoyage
    return () => {
      particles.current = [];
    };
  }, [isVisible]);
  
  // Animation des particules
  const animateParticles = () => {
    if (!particlesRef.current || !isVisible) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.current.forEach(particle => {
      // Mouvement
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      
      // Rebond sur les bords
      if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      
      // Dessin de la particule
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
      ctx.fill();
      
      // Connexion entre particules proches
      particles.current.forEach(otherParticle => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });
    });
    
    requestAnimationFrame(animateParticles);
  };
  
  // Effet de suivi du curseur
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };
    
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
      // Animation d'entrée
      setTimeout(() => setIsLoaded(true), 300);
      
      // Animation séquentielle des éléments
      const items = ['header', 'form', 'form-fields', 'contact-info', 'social-links'];
      let timer = 0;
      
      const animationInterval = setInterval(() => {
        if (timer < items.length) {
          setAnimatedItems(prev => [...prev, items[timer]]);
          timer++;
        } else {
          clearInterval(animationInterval);
        }
      }, 200);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        clearInterval(animationInterval);
        setAnimatedItems([]);
      };
    } else {
      setIsLoaded(false);
    }
  }, [isVisible]);
  
  // Helmet pour le SEO
  const helmet = isVisible ? (
    <Helmet>
      <title>Contact | Portfolio de Hamza TAKARROUHT</title>
      <meta name="description" content="Contactez Hamza TAKARROUHT pour discuter de vos projets de développement web ou pour toute autre information." />
    </Helmet>
  ) : null;
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleFocus = (field) => {
    setFocusedField(field);
  };
  
  const handleBlur = () => {
    setFocusedField(null);
  };
  
  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateContactForm(formData);
    
    if (!validation.isValid) {
      // Afficher les erreurs
      setFormErrors(validation.errors);
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: Object.values(validation.errors).join('. ')
      });
      return;
    }
    setFormErrors({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    // Vérifier si le recaptcha est validé
    if (!recaptchaValue) {
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'Veuillez valider le reCAPTCHA'
      });
      return;
    }
    
    setFormStatus({
      ...formStatus,
      isSubmitting: true
    });
    
    try {
      // Utilisation d'EmailJS pour l'envoi
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          'g-recaptcha-response': recaptchaValue
        },
        process.env.REACT_APP_EMAILJS_USER_ID
      );
      
      setFormStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        errorMessage: ''
      });
      
      // Réinitialiser le formulaire après succès
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Réinitialiser le reCAPTCHA
      setRecaptchaValue(null);
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'Une erreur est survenue lors de l\'envoi. Veuillez réessayer.'
      });
    }
  };
  
  // Définition de la position du gradient qui suit la souris
  const gradientPosition = `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(179, 135, 246, 0.2) 0%, rgba(92, 122, 250, 0.1) 40%, transparent 70%)`;
  
  return (
    <>
      {helmet}
      <div 
        className={`contact-section ${isVisible ? 'visible' : ''}`}
        aria-hidden={!isVisible}
        ref={containerRef}
      >
        {/* Effet de particules en arrière-plan */}
        <canvas 
          ref={particlesRef} 
          className="particles-canvas"
        />
        
        {/* Gradient qui suit la souris */}
        <div 
          className="mouse-gradient"
          style={{ background: gradientPosition }}
        />
        
        {/* Bouton de fermeture */}
        <div 
          className="contact-close" 
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose();
            }
          }}
          role="button"
          aria-label="Fermer la section Contact"
          tabIndex={isVisible ? 0 : -1}
        >
          <span></span>
          <span></span>
        </div>
        
        <div className={`contact-container ${isLoaded ? 'loaded' : ''}`}>
          <div className={`contact-header ${animatedItems.includes('header') ? 'animated' : ''}`}>
            <h2 className="contact-title">Contactez-moi</h2>
            <p className="contact-subtitle">
              <span>Discutons de vos projets innovants</span>
            </p>
          </div>
          
          <div className="contact-content">
            <div className="contact-grid">
              <div className={`contact-form-wrapper ${animatedItems.includes('form') ? 'animated' : ''}`}>
                <div className="form-card" ref={formRef}>
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className={`form-fields ${animatedItems.includes('form-fields') ? 'animated' : ''}`}>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="name" className={`form-label ${focusedField === 'name' || formData.name ? 'active' : ''}`}>
                            Nom complet *
                          </label>
                          <div className="input-wrapper">
                            <span className="input-icon">
                              <i className="fas fa-user"></i>
                            </span>
                            <input 
                              type="text" 
                              id="name" 
                              name="name" 
                              required 
                              pattern="^[a-zA-ZÀ-ÿ\s]{2,50}$" 
                              placeholder=""
                              value={formData.name}
                              onChange={handleChange}
                              onFocus={() => handleFocus('name')}
                              onBlur={handleBlur}
                              className={focusedField === 'name' ? 'focused' : ''}
                            />
                          </div>
                          <span className="input-highlight"></span>
                            {formErrors.name && <div className="error-message">{formErrors.name}</div>}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="email" className={`form-label ${focusedField === 'email' || formData.email ? 'active' : ''}`}>
                            Email *
                          </label>
                          <div className="input-wrapper">
                            <span className="input-icon">
                              <i className="fas fa-envelope"></i>
                            </span>
                            <input 
                              type="email" 
                              id="email" 
                              name="email" 
                              required 
                              placeholder=""
                              value={formData.email}
                              onChange={handleChange}
                              onFocus={() => handleFocus('email')}
                              onBlur={handleBlur}
                              className={focusedField === 'email' ? 'focused' : ''}
                            />
                          </div>
                          <span className="input-highlight"></span>
                          {formErrors.email && <div className="error-message">{formErrors.email}</div>}
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="subject" className={`form-label ${focusedField === 'subject' || formData.subject ? 'active' : ''}`}>
                          Sujet *
                        </label>
                        <div className="input-wrapper">
                          <span className="input-icon">
                            <i className="fas fa-heading"></i>
                          </span>
                          <input 
                            type="text" 
                            id="subject" 
                            name="subject" 
                            required 
                            maxLength="100" 
                            placeholder=""
                            value={formData.subject}
                            onChange={handleChange}
                            onFocus={() => handleFocus('subject')}
                            onBlur={handleBlur}
                            className={focusedField === 'subject' ? 'focused' : ''}
                          />
                        </div>
                        <span className="input-highlight"></span>
                        {formErrors.subject && <div className="error-message">{formErrors.subject}</div>}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="message" className={`form-label ${focusedField === 'message' || formData.message ? 'active' : ''}`}>
                          Message *
                        </label>
                        <div className="textarea-wrapper">
                          <textarea 
                            id="message" 
                            name="message" 
                            required 
                            minLength="10" 
                            maxLength="1000" 
                            placeholder=""
                            value={formData.message}
                            onChange={handleChange}
                            onFocus={() => handleFocus('message')}
                            onBlur={handleBlur}
                            className={focusedField === 'message' ? 'focused' : ''}
                          ></textarea>
                          <div className="character-count">
                            {1000 - formData.message.length} caractères restants
                          </div>
                        </div>
                        <span className="textarea-highlight"></span>
                        {formErrors.message && <div className="error-message">{formErrors.message}</div>}
                      </div>
                      
                      <div className="form-group recaptcha-wrapper">
                        <ReCAPTCHA
                          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                          onChange={handleRecaptchaChange}
                          theme="dark"
                        />
                      </div>
                      
                      <div className="form-submit">
                        <button 
                          type="submit" 
                          className={`submit-button ${formStatus.isSubmitting ? 'submitting' : ''}`}
                          disabled={formStatus.isSubmitting}
                        >
                          <span className="button-content">
                            <i className="fas fa-paper-plane"></i>
                            <span>{formStatus.isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}</span>
                          </span>
                          <span className="button-bg"></span>
                        </button>
                      </div>
                    </div>
                  </form>
                  
                  {/* Affichage des messages de statut */}
                  {(formStatus.isSuccess || formStatus.isError) && (
                    <div className={`form-status-message ${formStatus.isSuccess ? 'success' : 'error'}`}>
                      <div className="status-icon">
                        <i className={formStatus.isSuccess ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}></i>
                      </div>
                      <div className="status-content">
                        <h3>{formStatus.isSuccess ? 'Message envoyé !' : 'Erreur'}</h3>
                        <p>
                          {formStatus.isSuccess 
                            ? 'Merci pour votre message. Je vous répondrai dans les plus brefs délais.' 
                            : formStatus.errorMessage}
                        </p>
                      </div>
                      <button 
                        className="close-status" 
                        onClick={() => setFormStatus({ isSubmitting: false, isSuccess: false, isError: false, errorMessage: '' })}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="contact-info-wrapper">
                <div className={`contact-info-card ${animatedItems.includes('contact-info') ? 'animated' : ''}`}>
                  <div className="card-header">
                    <div className="info-icon">
                      <i className="fas fa-at"></i>
                    </div>
                    <h3>Contact direct</h3>
                  </div>
                  <div className="card-content">
                    <a 
                      href="mailto:hamza2001takarrouht@gmail.com" 
                      className="contact-link"
                    >
                      <i className="fas fa-envelope"></i>
                      <span>hamza2001takarrouht@gmail.com</span>
                    </a>
                    <div className="location-info">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>France</span>
                    </div>
                  </div>
                </div>
                
                <div className={`social-card ${animatedItems.includes('social-links') ? 'animated' : ''}`}>
                  <div className="card-header">
                    <div className="info-icon">
                      <i className="fas fa-share-alt"></i>
                    </div>
                    <h3>Réseaux sociaux</h3>
                  </div>
                  <div className="social-links">
                    <a 
                      href="https://linkedin.com/in/hamza-takarrouht-034a3a22b" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-link linkedin"
                      aria-label="LinkedIn"
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  </div>
                  
                  <div className="availability-status">
                    <div className="availability-indicator"></div>
                    <span>Disponible pour vos projets</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;